import { ReactElement } from 'react';
import { CmsImage } from '@zoopla/cms/image';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { CONTENTFUL_RICH_TEXT_OPTIONS } from '@zoopla/cms/contentful-render-options';
import {
    columnContainer,
    imageStyles,
    pictureStyles
} from './cms-two-column-split.styles.css';
import { TwoColumnSplit, ColumnContent } from '../graphql.generated';
import { BLOCKS, Node } from '@contentful/rich-text-types';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { Box } from '@zoopla-eng/zds-components/box';
import { ButtonEntry } from '@zoopla/cms/contentful-render-options';
import { isDocument } from '@zoopla/cms/types';

/* Here we have to override the way Buttons embedded in rich text fields
 * are rendered, due to design guidelines.
 * Buttons in this component should not be full width on desktops.
 */
const OVERRIDE_RICH_TEXT_OPTIONS = {
    ...CONTENTFUL_RICH_TEXT_OPTIONS,
    renderNode: {
        ...CONTENTFUL_RICH_TEXT_OPTIONS.renderNode,
        [BLOCKS.PARAGRAPH]: (_node: Node, children: React.ReactNode) => {
            const isEmptyChildren = children?.toString().trim() === '';
            if (isEmptyChildren) return null;

            return <p className={`${fontGroups.whisk}`}>{children}</p>;
        },
        [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
            switch (node.data['target'].sys.contentType.sys.id) {
                case 'button':
                    return (
                        <Box display="flex">
                            <ButtonEntry
                                buttonUrl={node.data['target'].fields.buttonUrl}
                                category={node.data['target'].fields.category}
                                buttonLabel={
                                    node.data['target'].fields.buttonLabel
                                }
                                eventTracking={
                                    node.data['target'].fields.eventTracking ??
                                    undefined
                                }
                            />
                        </Box>
                    );
                default:
                    return null;
            }
        }
    }
};

function renderColumn(column: ColumnContent): ReactElement | null {
    switch (column.__typename) {
        case 'ContentfulImage':
            return (
                <Box flex={1}>
                    <CmsImage
                        width={768}
                        src={column.url}
                        alt={column.altText || ''}
                        imgClassName={imageStyles}
                        pictureClassName={pictureStyles}
                        sizes="(min-width: 768px) 600px, 100vw"
                    />
                </Box>
            );
        case 'ComponentRichText':
            if (!isDocument(column.content)) {
                return null;
            }
            return (
                <Box display="flex" alignItems="center" flex={1}>
                    <Box display="flex" rowGap="S400" flexDirection="column">
                        {documentToReactComponents(
                            column.content,
                            OVERRIDE_RICH_TEXT_OPTIONS
                        )}
                    </Box>
                </Box>
            );
        default:
            return null;
    }
}

function CmsTwoColumnSplit(props: TwoColumnSplit) {
    return (
        <div className={columnContainer}>
            {renderColumn(props.leftColumn)}
            {renderColumn(props.rightColumn)}
        </div>
    );
}

export default CmsTwoColumnSplit;
